import type { ReactElement, ChangeEvent } from 'react';
import type { StoryVersion } from '@/types/story';
import type { User } from '@/types/user';
import type { Job } from '@/types/job';

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import PageLayout from '@/components/PageLayout';
import StorySettingsModal from '@/components/StorySettingsModal';
import VersionBadge from '@/components/VersionBadge';
import { BookOpen, Plus, Sparkles, Lock, Loader2, ArrowRight, XCircle, RefreshCw, Search, Eye, Heart, ChevronDown, MoreHorizontal, Star, Film } from 'lucide-react';
import { toast } from 'sonner';
import { getBookTypeLabel } from '@/lib/utils';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ITEMS_PER_PAGE = 10;

/**
 * Story page structure (abbreviated for library list)
 */
interface StoryPage {
  page_number: number;
  text?: string;
  image_prompt?: string;
  image_url?: string;
  audio_url?: string;
}

/**
 * Library story list item
 */
interface LibraryStory {
  id: string;
  title: string;
  prompt?: string;
  age_range: string;
  category?: string;
  art_style?: string;
  is_public?: boolean;
  thumbnail?: string;
  pages?: StoryPage[];
  version?: StoryVersion;
  is_pro_story?: boolean;
  views_count?: number;
  favorites_count?: number;
  created_at: string;
  has_animations?: boolean;
}

/**
 * Library API response
 */
interface LibraryResponse {
  stories: LibraryStory[];
  total: number;
  hasMore: boolean;
}

/**
 * Jobs API response
 */
interface JobsResponse {
  jobs: Job[];
}

/**
 * User API response (from /auth/me)
 */
interface AuthMeResponse {
  user_id: string;
  email: string;
  name: string;
  picture?: string;
  is_premium: boolean;
  is_admin: boolean;
  credits: number;
  stories_generated: number;
  email_verified: boolean;
  has_purchased_credits: boolean;
  created_at: string;
  updated_at?: string;
}

/**
 * Job retry response
 */
interface JobRetryResponse {
  job_id: string;
}

/**
 * Location state from navigation
 */
interface LocationState {
  user?: User;
}

type TabType = 'stories' | 'favorites';
type SortType = 'newest' | 'oldest' | 'views' | 'favorites';
type VisibilityFilterType = 'all' | 'public' | 'private';
type ProFilterType = 'all' | 'pro' | 'standard';
type AnimatedFilterType = 'all' | 'animated' | 'static';

interface SortOption {
  value: SortType;
  label: string;
}

interface VisibilityOption {
  value: VisibilityFilterType;
  label: string;
}

interface TypeOption {
  value: ProFilterType;
  label: string;
  icon?: boolean;
}

interface AnimatedOption {
  value: AnimatedFilterType;
  label: string;
  icon?: boolean;
}

// Helper to get full image URL
const getImageUrl = (url: string | undefined | null): string | null => {
  if (!url) return null;
  if (url.startsWith('/static/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

interface InProgressStoryCardProps {
  job: Job;
  onNavigate: (path: string) => void;
}

const InProgressStoryCard = ({ job, onNavigate }: InProgressStoryCardProps): ReactElement => (
  <div
    className="flex items-center gap-4 p-3 rounded-xl hover:bg-black/5 dark:hover:bg-white/10 transition-all duration-200 cursor-pointer group"
    onClick={() => onNavigate(`/generating/${job.id}`)}
    style={{ borderBottom: '1px solid var(--st-border-light)', background: 'var(--st-bg-surface)' }}
  >
    {/* Thumbnail with sparkles icon (matches GeneratingPage) */}
    <div className="relative w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden flex-shrink-0">
      {job.thumbnail_url ? (
        <>
          <img src={getImageUrl(job.thumbnail_url) || undefined} alt="Preview" className="w-full h-full object-cover" />
          {/* Spinner overlay on thumbnail */}
          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
            <Loader2 className="w-6 h-6 text-white animate-spin" />
          </div>
        </>
      ) : (
        <div
          className="w-full h-full flex items-center justify-center relative"
          style={{ background: 'linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)' }}
        >
          <Sparkles className="w-6 h-6 text-white/80" />
          {/* Spinner overlay */}
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-white animate-spin" />
          </div>
        </div>
      )}
    </div>

    {/* Content */}
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <h3 className="font-bold text-sm md:text-base truncate" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
          Creating Story...
        </h3>
        <span className="px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 animate-pulse" style={{ background: 'var(--st-border-light)', color: 'var(--st-primary)' }}>
          {job.status === 'queued' ? `Queue #${job.queue_position}` : `${job.progress || 0}%`}
        </span>
      </div>
      <p className="text-xs md:text-sm text-gray-500 truncate" style={{ fontFamily: 'Quicksand, sans-serif' }}>
        {job.prompt?.length > 60 ? job.prompt.substring(0, 60) + '...' : job.prompt}
      </p>
      {/* Progress bar */}
      {job.status === 'processing' && (
        <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--st-border-light)', maxWidth: '200px' }}>
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${job.progress || 0}%`,
              background: 'linear-gradient(90deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)'
            }}
          />
        </div>
      )}
      <p className="text-xs text-gray-400 mt-1" style={{ fontFamily: 'Quicksand, sans-serif' }}>
        {job.message || 'Processing...'}
      </p>
    </div>

    {/* Arrow */}
    <ArrowRight className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--st-primary)' }} />
  </div>
);

interface StoryListItemProps {
  story: LibraryStory;
  isOwnStory?: boolean;
  onOpenSettings: (e: React.MouseEvent, story: LibraryStory) => void;
  onNavigate: (path: string) => void;
  formatDate: (dateString: string) => string;
}

const StoryListItem = ({ story, isOwnStory = true, onOpenSettings, onNavigate, formatDate }: StoryListItemProps): ReactElement => (
  <div
    className="flex items-center gap-4 p-3 rounded-xl hover:bg-black/5 dark:hover:bg-white/10 hover:scale-[1.01] transition-all duration-200 cursor-pointer group"
    onClick={() => onNavigate(`/story/${story.id}`)}
    style={{ borderBottom: '1px solid var(--st-border-light)' }}
  >
    {/* Thumbnail with page count overlay */}
    <div className="relative w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden flex-shrink-0">
      {story.thumbnail ? (
        <img src={getImageUrl(story.thumbnail) || undefined} alt={story.title} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, var(--st-border-light) 0%, var(--st-border-medium) 100%)' }}>
          <Sparkles className="w-6 h-6" style={{ color: 'var(--st-primary-hover)' }} />
        </div>
      )}
      <div className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded text-xs font-bold text-white" style={{ background: 'rgba(0,0,0,0.6)', fontSize: '10px' }}>
        {story.pages?.length || 0}p
      </div>
    </div>

    {/* Content */}
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <h3 className="font-bold text-sm md:text-base truncate" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
          {story.title}
        </h3>
        <span className="px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0" style={{ background: 'var(--st-bg-muted)', color: 'var(--st-text-heading)' }}>
          {getBookTypeLabel(story.age_range)}
        </span>
        <VersionBadge version={story.version} isProStory={story.is_pro_story} size="small" />
        {story.has_animations && (
          <span title="Animated Pages">
            <Film className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#F59E0B' }} />
          </span>
        )}
        {story.is_public === false && (
          <Lock className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--st-text-muted)' }} />
        )}
      </div>
      <p className="text-xs md:text-sm text-gray-500 truncate" style={{ fontFamily: 'Quicksand, sans-serif' }}>
        {story.prompt}
      </p>
      <p className="text-xs text-gray-400 mt-1" style={{ fontFamily: 'Quicksand, sans-serif' }}>
        {formatDate(story.created_at)}
      </p>
    </div>

    {/* Stats */}
    <div className="hidden md:flex items-center gap-4 text-xs flex-shrink-0" style={{ color: 'var(--st-text-muted)' }}>
      <span className="flex items-center gap-1" title="Views">
        <Eye className="w-4 h-4" />
        {story.views_count || 0}
      </span>
      <span className="flex items-center gap-1" title="Likes">
        <Heart className="w-4 h-4" style={{ color: story.favorites_count ? '#EC4899' : 'var(--st-text-muted)' }} />
        {story.favorites_count || 0}
      </span>
    </div>

    {/* Actions - Only show for user's own stories */}
    {isOwnStory && (
      <div className="flex items-center gap-2 flex-shrink-0 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
        <button
          onClick={(e) => onOpenSettings(e, story)}
          className="p-2 rounded-xl transition-all library-settings-btn"
          title="Settings"
        >
          <MoreHorizontal className="w-5 h-5" style={{ color: 'var(--st-primary)' }} />
        </button>
      </div>
    )}
  </div>
);

const LibraryPage = (): ReactElement => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState | null;
  const [user, setUser] = useState<User | null>(() => {
    if (locationState?.user) return locationState.user;
    try {
      const cached = localStorage.getItem("cached_user");
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [stories, setStories] = useState<LibraryStory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(locationState?.user ? true : null);
  const [selectedStory, setSelectedStory] = useState<LibraryStory | null>(null);
  const [showSettingsModal, setShowSettingsModal] = useState<boolean>(false);
  const [activeJobs, setActiveJobs] = useState<Job[]>([]);
  const [failedJobs, setFailedJobs] = useState<Job[]>([]);
  const [retryingJobId, setRetryingJobId] = useState<string | null>(null);

  // New state for Suno-style features
  const [activeTab, setActiveTab] = useState<TabType>('stories');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortBy, setSortBy] = useState<SortType>('newest');
  const [visibilityFilter, setVisibilityFilter] = useState<VisibilityFilterType>('all');
  const [proFilter, setProFilter] = useState<ProFilterType>('all');
  const [animatedFilter, setAnimatedFilter] = useState<AnimatedFilterType>('all');
  const [hasMore, setHasMore] = useState<boolean>(true);

  // Dropdown open states
  const [sortDropdownOpen, setSortDropdownOpen] = useState<boolean>(false);
  const [visibilityDropdownOpen, setVisibilityDropdownOpen] = useState<boolean>(false);
  const [typeDropdownOpen, setTypeDropdownOpen] = useState<boolean>(false);
  const [animatedDropdownOpen, setAnimatedDropdownOpen] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  const [favorites, setFavorites] = useState<LibraryStory[]>([]);

  // Track if initial load has completed to prevent duplicate loads
  const initialLoadComplete = useRef<boolean>(false);
  // Track previous active job count to detect completions
  const prevActiveJobCount = useRef<number>(0);
  // Flag to continue polling for a bit after jobs appear to finish
  const pollGraceCounter = useRef<number>(0);

  const handleOpenSettings = (e: React.MouseEvent, story: LibraryStory): void => {
    e.stopPropagation();
    setSelectedStory(story);
    setShowSettingsModal(true);
  };

  const handleStoryUpdate = (updatedStory: LibraryStory): void => {
    setStories(stories.map(s => s.id === updatedStory.id ? updatedStory : s));
  };

  const handleStoryDelete = (storyId: string): void => {
    setStories(stories.filter(s => s.id !== storyId));
  };

  const loadJobs = async (): Promise<void> => {
    try {
      // Don't use .catch() that returns empty arrays - let errors propagate
      // so we keep previous state on network hiccups instead of clearing jobs
      const [activeJobsRes, allJobsRes] = await Promise.all([
        axios.get<JobsResponse>(`${API}/v1/jobs/mine?include_completed=false`, { withCredentials: true }),
        axios.get<JobsResponse>(`${API}/v1/jobs/mine?include_completed=true&limit=10`, { withCredentials: true })
      ]);

      const allJobs = allJobsRes.data.jobs || [];
      const active = (activeJobsRes.data.jobs || []).filter(j =>
        j.status === 'queued' || j.status === 'processing'
      );

      // Grace period logic: only apply when jobs disappear unexpectedly (not completed)
      // This prevents jobs from disappearing due to momentary API hiccups
      if (active.length === 0 && activeJobs.length > 0) {
        // Check if our previously active jobs are now in completed/failed state
        const previousJobIds = activeJobs.map(j => j.id);
        const completedOrFailed = allJobs.filter(j =>
          previousJobIds.includes(j.id) && (j.status === 'completed' || j.status === 'failed')
        );

        // If jobs legitimately completed/failed, no grace period needed
        if (completedOrFailed.length === activeJobs.length) {
          pollGraceCounter.current = 0;
        } else {
          // Jobs disappeared without completing - apply grace period
          pollGraceCounter.current += 1;
          if (pollGraceCounter.current < 5) {
            console.log(`Job grace period: poll ${pollGraceCounter.current}/5 (jobs disappeared unexpectedly)`);
            return;
          }
        }
      } else {
        pollGraceCounter.current = 0;
      }

      setActiveJobs(active);

      const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const failed = allJobs.filter(j =>
        j.status === 'failed' && j.created_at > oneDayAgo
      );
      setFailedJobs(failed);
    } catch (error) {
      // On error, keep the previous activeJobs state - don't clear
      // This prevents jobs from disappearing on network hiccups
      console.error('Failed to load jobs (keeping previous state):', error);
    }
  };

  const loadStories = async (reset: boolean = false): Promise<void> => {
    try {
      // Only show loading state if we have no content to display
      // This prevents flicker when refreshing with existing content
      if (reset && stories.length === 0 && activeJobs.length === 0) {
        setLoading(true);
      }

      const offset = reset ? 0 : stories.length;
      const params = new URLSearchParams({
        limit: ITEMS_PER_PAGE.toString(),
        offset: offset.toString(),
        sort: sortBy,
        visibility: visibilityFilter,
        pro_filter: proFilter,
        animated_filter: animatedFilter
      });

      if (searchQuery.trim()) {
        params.append('q', searchQuery.trim());
      }

      const response = await axios.get<LibraryResponse>(`${API}/v1/stories/library?${params}`, {
        withCredentials: true
      });

      const { stories: newStories, total: totalCount, hasMore: more } = response.data;

      if (reset) {
        setStories(newStories);
      } else {
        // Deduplicate by ID to prevent duplicates on pagination
        setStories(prev => {
          const existingIds = new Set(prev.map(s => s.id));
          const uniqueNewStories = newStories.filter(s => !existingIds.has(s.id));
          return [...prev, ...uniqueNewStories];
        });
      }

      setTotal(totalCount);
      setHasMore(more);
    } catch (error) {
      console.error('Failed to load stories:', error);
      toast.error('Failed to load stories');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadFavorites = async (): Promise<void> => {
    // Only show loading if we have no favorites yet
    if (favorites.length === 0) {
      setLoading(true);
    }
    try {
      const response = await axios.get<{ stories: LibraryStory[]; total: number; hasMore: boolean }>(`${API}/stories/favorites`, {
        withCredentials: true
      });
      setFavorites(response.data.stories);
    } catch (error) {
      console.error('Failed to load favorites:', error);
      toast.error('Failed to load favorites');
    } finally {
      setLoading(false);
    }
  };

  const loadInitialData = async (): Promise<void> => {
    await Promise.all([
      loadStories(true),
      loadJobs()
    ]);
    initialLoadComplete.current = true;
  };

  useEffect(() => {
    if (locationState?.user) {
      setIsAuthenticated(true);
      loadInitialData();
      return;
    }

    const checkAuth = async (): Promise<void> => {
      try {
        const response = await axios.get<AuthMeResponse>(`${API}/auth/me`, {
          withCredentials: true
        });
        setUser(response.data as unknown as User);
        setIsAuthenticated(true);
        loadInitialData();
      } catch (error) {
        setIsAuthenticated(false);
        navigate('/login');
      }
    };

    checkAuth();
  }, [navigate, location]);

  // Reload when filters change (skip initial mount - loadInitialData handles it)
  useEffect(() => {
    if (!initialLoadComplete.current) return;
    if (isAuthenticated && activeTab === 'stories') {
      loadStories(true);
    }
  }, [sortBy, visibilityFilter, proFilter, animatedFilter, searchQuery]);

  // Load favorites when tab changes
  useEffect(() => {
    if (isAuthenticated && activeTab === 'favorites') {
      loadFavorites();
    }
  }, [activeTab]);

  // Poll for job updates when there are active jobs
  useEffect(() => {
    if (!isAuthenticated || activeJobs.length === 0) return;

    const pollInterval = setInterval(() => {
      loadJobs();
    }, 3000); // Poll every 3 seconds

    return () => clearInterval(pollInterval);
  }, [isAuthenticated, activeJobs.length]);

  // Reload stories when a job completes (activeJobs count decreases)
  useEffect(() => {
    if (!initialLoadComplete.current) return;

    // If we had active jobs before and now have fewer, a job completed
    if (prevActiveJobCount.current > 0 && activeJobs.length < prevActiveJobCount.current) {
      loadStories(true);
    }
    prevActiveJobCount.current = activeJobs.length;
  }, [activeJobs.length]);

  const loadMore = async (): Promise<void> => {
    if (activeTab === 'stories' && hasMore && !loadingMore) {
      await loadStories(false);
    }
  };

  // Infinite scroll handler
  const handleLoadMore = useCallback(() => {
    loadMore();
  }, [activeTab, stories.length, hasMore]);

  // Hook up infinite scroll - returns a ref to attach to loader element
  const loaderRef = useInfiniteScroll({
    onLoadMore: handleLoadMore,
    hasMore: activeTab === 'stories' ? hasMore : false,
    loading: loading || loadingMore
  });

  const handleRetryJob = async (jobId: string): Promise<void> => {
    setRetryingJobId(jobId);
    try {
      const response = await axios.post<JobRetryResponse>(`${API}/v1/jobs/${jobId}/retry`, {}, {
        withCredentials: true
      });
      toast.success('Retrying your story!');
      navigate(`/generating/${response.data.job_id}`);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const errorMsg = err.response?.data?.detail || 'Failed to retry. Please try again.';
        toast.error(errorMsg);
      } else {
        toast.error('Failed to retry. Please try again.');
      }
      setRetryingJobId(null);
    }
  };

  const handleDismissFailedJob = async (jobId: string): Promise<void> => {
    try {
      await axios.delete(`${API}/v1/jobs/${jobId}/dismiss`, {
        withCredentials: true
      });
      setFailedJobs(failedJobs.filter(j => j.id !== jobId));
    } catch (err) {
      console.error('Failed to dismiss job:', err);
      setFailedJobs(failedJobs.filter(j => j.id !== jobId));
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Debounced search
  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>): void => {
    setSearchQuery(e.target.value);
  }, []);

  const sortOptions: SortOption[] = [
    { value: 'newest', label: 'Newest' },
    { value: 'oldest', label: 'Oldest' },
    { value: 'views', label: 'Most Viewed' },
    { value: 'favorites', label: 'Most Liked' }
  ];

  const visibilityOptions: VisibilityOption[] = [
    { value: 'all', label: 'All' },
    { value: 'public', label: 'Public Only' },
    { value: 'private', label: 'Private Only' }
  ];

  const typeOptions: TypeOption[] = [
    { value: 'all', label: 'All' },
    { value: 'pro', label: 'Pro Only', icon: true },
    { value: 'standard', label: 'Standard Only' }
  ];

  const animatedOptions: AnimatedOption[] = [
    { value: 'all', label: 'All' },
    { value: 'animated', label: 'Animated', icon: true },
    { value: 'static', label: 'Static' }
  ];

  const sortLabels: Record<SortType, string> = {
    newest: 'Newest',
    oldest: 'Oldest',
    views: 'Most Viewed',
    favorites: 'Most Liked'
  };

  const visibilityLabels: Record<VisibilityFilterType, string> = {
    all: 'Visibility: All',
    public: 'Public Only',
    private: 'Private Only'
  };

  const typeLabels: Record<ProFilterType, string> = {
    all: 'Type: All',
    pro: 'Pro Only',
    standard: 'Standard Only'
  };

  const animatedLabels: Record<AnimatedFilterType, string> = {
    all: 'Animation: All',
    animated: 'Animated',
    static: 'Static'
  };

  // Only show full-page loading on initial load (no content yet)
  // Don't show if we have stories OR active jobs to display
  if (isAuthenticated === null || (loading && stories.length === 0 && activeJobs.length === 0)) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center min-h-[calc(100vh-60px)] md:min-h-screen">
          <div className="text-center">
            <BookOpen className="w-12 h-12 mx-auto mb-4 animate-pulse" style={{ color: 'var(--st-primary)' }} />
            <p style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>Loading your library...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  const displayedStories = activeTab === 'favorites' ? favorites : stories;

  return (
    <PageLayout>
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-screen-2xl mx-auto px-4 py-4 md:py-6 md:px-8 2xl:px-12">

          {/* Header */}
          <div className="rounded-2xl p-4 md:p-5 mb-5 shadow-sm" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl md:text-2xl font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                  My Library
                </h1>
                <p className="text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                  {total} {total === 1 ? 'story' : 'stories'} created
                </p>
              </div>
              <button
                onClick={() => navigate('/create')}
                className="hidden sm:flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm"
                style={{
                  background: 'linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)',
                  color: 'white',
                  fontFamily: 'Quicksand, sans-serif'
                }}
              >
                <Sparkles className="w-4 h-4" />
                Create Story
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 p-1 mb-5 rounded-xl" style={{ background: 'var(--st-bg-muted)' }}>
            <button
              onClick={() => setActiveTab('stories')}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                activeTab === 'stories' ? 'shadow-sm' : 'hover:bg-white/50 dark:hover:bg-white/10'
              }`}
              style={{
                fontFamily: 'Quicksand, sans-serif',
                color: activeTab === 'stories' ? 'var(--st-text-heading)' : 'var(--st-text-muted)',
                ...(activeTab === 'stories' ? { background: 'var(--st-bg-surface)' } : {})
              }}
            >
              <BookOpen className="w-4 h-4" style={{ color: activeTab === 'stories' ? 'var(--st-primary)' : 'var(--st-text-muted)' }} />
              My Stories
            </button>
            <button
              onClick={() => setActiveTab('favorites')}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                activeTab === 'favorites' ? 'shadow-sm' : 'hover:bg-white/50 dark:hover:bg-white/10'
              }`}
              style={{
                fontFamily: 'Quicksand, sans-serif',
                color: activeTab === 'favorites' ? 'var(--st-text-heading)' : 'var(--st-text-muted)',
                ...(activeTab === 'favorites' ? { background: 'var(--st-bg-surface)' } : {})
              }}
            >
              <Heart className="w-4 h-4" style={{ color: activeTab === 'favorites' ? 'var(--st-primary)' : 'var(--st-text-muted)' }} />
              Favorites
            </button>
          </div>

          {/* Search & Filters Row */}
          {activeTab === 'stories' && (
            <div className="flex flex-col gap-3 mb-5">
              {/* Search Input */}
              <div className="relative w-full">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: 'var(--st-text-muted)' }} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Search your stories..."
                  className="w-full pl-11 pr-4 py-2.5 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 dark:focus:ring-orange-200/30 transition-all shadow-sm"
                  style={{
                    fontFamily: 'Quicksand, sans-serif',
                    border: '1px solid var(--st-border-light)',
                    background: 'var(--st-bg-surface)',
                    color: 'var(--st-text-heading)'
                  }}
                />
              </div>

              {/* Filter Dropdowns Row */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                {/* Sort Dropdown */}
                <div className="relative w-full sm:w-auto">
                  <button
                    onClick={() => { setSortDropdownOpen(!sortDropdownOpen); setVisibilityDropdownOpen(false); setTypeDropdownOpen(false); setAnimatedDropdownOpen(false); }}
                    className="w-full sm:w-auto flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium cursor-pointer focus:outline-none transition-all shadow-sm"
                    style={{
                      fontFamily: 'Quicksand, sans-serif',
                      border: '1px solid var(--st-border-light)',
                      background: 'var(--st-bg-surface)',
                      color: 'var(--st-text-heading)',
                      minWidth: '130px'
                    }}
                  >
                  <span>{sortLabels[sortBy]}</span>
                  <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${sortDropdownOpen ? 'rotate-180' : ''}`} style={{ color: 'var(--st-text-muted)' }} />
                </button>

                {sortDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setSortDropdownOpen(false)} />
                    <div className="absolute left-0 top-full mt-2 z-20 rounded-xl shadow-lg overflow-hidden" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)', minWidth: '150px' }}>
                      {sortOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => { setSortBy(option.value); setSortDropdownOpen(false); }}
                          className="w-full px-4 py-2.5 text-left text-sm font-medium transition-colors hover:bg-black/5 dark:hover:bg-white/10 flex items-center gap-2"
                          style={{
                            fontFamily: 'Quicksand, sans-serif',
                            color: sortBy === option.value ? 'var(--st-primary)' : 'var(--st-text-heading)',
                            background: 'var(--st-bg-surface)'
                          }}
                        >
                          {sortBy === option.value && <span style={{ color: 'var(--st-primary)' }}>✓</span>}
                          <span className={sortBy === option.value ? '' : 'ml-5'}>{option.label}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Visibility Filter Dropdown */}
              <div className="relative w-full sm:w-auto">
                <button
                  onClick={() => { setVisibilityDropdownOpen(!visibilityDropdownOpen); setSortDropdownOpen(false); setTypeDropdownOpen(false); setAnimatedDropdownOpen(false); }}
                  className="w-full sm:w-auto flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium cursor-pointer focus:outline-none transition-all shadow-sm"
                  style={{
                    fontFamily: 'Quicksand, sans-serif',
                    border: '1px solid var(--st-border-light)',
                    background: 'var(--st-bg-surface)',
                    color: 'var(--st-text-heading)',
                    minWidth: '140px'
                  }}
                >
                  <span>{visibilityLabels[visibilityFilter]}</span>
                  <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${visibilityDropdownOpen ? 'rotate-180' : ''}`} style={{ color: 'var(--st-text-muted)' }} />
                </button>

                {visibilityDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setVisibilityDropdownOpen(false)} />
                    <div className="absolute left-0 top-full mt-2 z-20 rounded-xl shadow-lg overflow-hidden" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)', minWidth: '150px' }}>
                      {visibilityOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => { setVisibilityFilter(option.value); setVisibilityDropdownOpen(false); }}
                          className="w-full px-4 py-2.5 text-left text-sm font-medium transition-colors hover:bg-black/5 dark:hover:bg-white/10 flex items-center gap-2"
                          style={{
                            fontFamily: 'Quicksand, sans-serif',
                            color: visibilityFilter === option.value ? 'var(--st-primary)' : 'var(--st-text-heading)',
                            background: 'var(--st-bg-surface)'
                          }}
                        >
                          {visibilityFilter === option.value && <span style={{ color: 'var(--st-primary)' }}>✓</span>}
                          <span className={visibilityFilter === option.value ? '' : 'ml-5'}>{option.label}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Type Filter Dropdown */}
              <div className="relative w-full sm:w-auto">
                <button
                  onClick={() => { setTypeDropdownOpen(!typeDropdownOpen); setSortDropdownOpen(false); setVisibilityDropdownOpen(false); setAnimatedDropdownOpen(false); }}
                  className="w-full sm:w-auto flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium cursor-pointer focus:outline-none transition-all shadow-sm"
                  style={{
                    fontFamily: 'Quicksand, sans-serif',
                    border: '1px solid var(--st-border-light)',
                    background: 'var(--st-bg-surface)',
                    color: 'var(--st-text-heading)',
                    minWidth: '130px'
                  }}
                >
                  <span className="flex items-center gap-1">
                    {proFilter === 'pro' && <Star className="w-3 h-3" style={{ color: '#9B59B6' }} />}
                    {typeLabels[proFilter]}
                  </span>
                  <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${typeDropdownOpen ? 'rotate-180' : ''}`} style={{ color: 'var(--st-text-muted)' }} />
                </button>

                {typeDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setTypeDropdownOpen(false)} />
                    <div className="absolute left-0 top-full mt-2 z-20 rounded-xl shadow-lg overflow-hidden" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)', minWidth: '150px' }}>
                      {typeOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => { setProFilter(option.value); setTypeDropdownOpen(false); }}
                          className="w-full px-4 py-2.5 text-left text-sm font-medium transition-colors hover:bg-black/5 dark:hover:bg-white/10 flex items-center gap-2"
                          style={{
                            fontFamily: 'Quicksand, sans-serif',
                            color: proFilter === option.value ? (option.value === 'pro' ? '#9B59B6' : 'var(--st-primary)') : 'var(--st-text-heading)',
                            background: 'var(--st-bg-surface)'
                          }}
                        >
                          {proFilter === option.value && <span style={{ color: option.value === 'pro' ? '#9B59B6' : 'var(--st-primary)' }}>✓</span>}
                          <span className={`flex items-center gap-1 ${proFilter === option.value ? '' : 'ml-5'}`}>
                            {option.icon && <Star className="w-3 h-3" style={{ color: '#9B59B6' }} />}
                            {option.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Animated Filter Dropdown */}
              <div className="relative w-full sm:w-auto">
                <button
                  onClick={() => { setAnimatedDropdownOpen(!animatedDropdownOpen); setSortDropdownOpen(false); setVisibilityDropdownOpen(false); setTypeDropdownOpen(false); }}
                  className="w-full sm:w-auto flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium cursor-pointer focus:outline-none transition-all shadow-sm"
                  style={{
                    fontFamily: 'Quicksand, sans-serif',
                    border: '1px solid var(--st-border-light)',
                    background: 'var(--st-bg-surface)',
                    color: 'var(--st-text-heading)',
                    minWidth: '130px'
                  }}
                >
                  <span className="flex items-center gap-1">
                    {animatedFilter === 'animated' && <Film className="w-3 h-3" style={{ color: '#F59E0B' }} />}
                    {animatedLabels[animatedFilter]}
                  </span>
                  <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${animatedDropdownOpen ? 'rotate-180' : ''}`} style={{ color: 'var(--st-text-muted)' }} />
                </button>

                {animatedDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setAnimatedDropdownOpen(false)} />
                    <div className="absolute left-0 top-full mt-2 z-20 rounded-xl shadow-lg overflow-hidden" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)', minWidth: '150px' }}>
                      {animatedOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => { setAnimatedFilter(option.value); setAnimatedDropdownOpen(false); }}
                          className="w-full px-4 py-2.5 text-left text-sm font-medium transition-colors hover:bg-black/5 dark:hover:bg-white/10 flex items-center gap-2"
                          style={{
                            fontFamily: 'Quicksand, sans-serif',
                            color: animatedFilter === option.value ? (option.value === 'animated' ? '#F59E0B' : 'var(--st-primary)') : 'var(--st-text-heading)',
                            background: 'var(--st-bg-surface)'
                          }}
                        >
                          {animatedFilter === option.value && <span style={{ color: option.value === 'animated' ? '#F59E0B' : 'var(--st-primary)' }}>✓</span>}
                          <span className={`flex items-center gap-1 ${animatedFilter === option.value ? '' : 'ml-5'}`}>
                            {option.icon && <Film className="w-3 h-3" style={{ color: '#F59E0B' }} />}
                            {option.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
              </div>
            </div>
          )}


          {/* Failed Jobs Section */}
          {failedJobs.length > 0 && (
            <div className="mb-5">
              <div className="flex items-center gap-2 mb-3">
                <XCircle className="w-4 h-4 text-red-400" />
                <h2 className="text-base font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                  Failed
                </h2>
              </div>
              <div className="space-y-2">
                {failedJobs.map((job) => (
                  <div
                    key={job.id}
                    className="rounded-xl p-3 shadow-sm flex items-center justify-between"
                    style={{ background: 'var(--st-bg-surface)', border: '1px solid #FEE2E2' }}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#FEE2E2' }}>
                        <XCircle className="w-5 h-5 text-red-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-bold text-sm truncate" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>
                          {job.prompt?.length > 40 ? job.prompt.substring(0, 40) + '...' : job.prompt}
                        </p>
                        <p className="text-xs text-gray-400 truncate">{job.error || 'Generation failed'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                      <button
                        onClick={() => handleRetryJob(job.id)}
                        disabled={retryingJobId === job.id}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-white disabled:opacity-50"
                        style={{ background: 'linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)' }}
                      >
                        {retryingJobId === job.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                        Retry
                      </button>
                      <button
                        onClick={() => handleDismissFailedJob(job.id)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-black/5 dark:hover:bg-white/10"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty State - only show if no stories AND no active jobs */}
          {displayedStories.length === 0 && activeJobs.length === 0 && !loading && (
            <div className="flex flex-col items-center justify-center py-16 rounded-2xl shadow-sm" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
              <BookOpen className="w-12 h-12 mb-4" style={{ color: 'var(--st-border-medium)' }} />
              <p className="text-lg font-bold mb-2 text-center" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                {activeTab === 'favorites' ? 'No Favorites Yet' : searchQuery ? 'No Stories Found' : 'No Stories Yet'}
              </p>
              <p className="text-sm mb-6 text-center" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                {activeTab === 'favorites'
                  ? 'Stories you favorite will appear here'
                  : searchQuery
                    ? 'Try a different search term'
                    : 'Create your first magical story!'}
              </p>
              {activeTab === 'stories' && !searchQuery && (
                <button
                  onClick={() => navigate('/create')}
                  className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all hover:scale-105"
                  style={{
                    background: 'linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)',
                    color: 'white',
                    fontFamily: 'Quicksand, sans-serif'
                  }}
                >
                  <Sparkles className="w-4 h-4" />
                  Create Story
                </button>
              )}
            </div>
          )}

          {/* Stories List - includes in-progress stories at top */}
          {(displayedStories.length > 0 || (activeTab === 'stories' && activeJobs.length > 0)) && (
            <div className="rounded-2xl overflow-hidden shadow-sm" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
              {/* In-progress stories at top (only on My Stories tab) */}
              {activeTab === 'stories' && activeJobs.map((job) => (
                <InProgressStoryCard key={`job-${job.id}`} job={job} onNavigate={navigate} />
              ))}
              {/* Completed stories */}
              {displayedStories.map((story) => (
                <StoryListItem
                  key={story.id}
                  story={story}
                  isOwnStory={activeTab === 'stories'}
                  onOpenSettings={handleOpenSettings}
                  onNavigate={navigate}
                  formatDate={formatDate}
                />
              ))}
            </div>
          )}

          {/* Infinite scroll trigger element */}
          {activeTab === 'stories' && hasMore && stories.length > 0 && (
            <div ref={loaderRef} className="flex justify-center py-6 mt-4">
              {loadingMore && (
                <>
                  <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--st-primary)' }} />
                  <p className="text-sm ml-2" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                    Loading more stories... ({stories.length} of {total})
                  </p>
                </>
              )}
            </div>
          )}

          {/* End of Results */}
          {activeTab === 'stories' && !hasMore && stories.length > 0 && (
            <div className="text-center py-6 mt-4">
              <p className="text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                You've reached the end! ({total} {total === 1 ? 'story' : 'stories'})
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Story Settings Modal */}
      <StorySettingsModal
        story={selectedStory as Parameters<typeof StorySettingsModal>[0]['story']}
        isOpen={showSettingsModal}
        onClose={() => {
          setShowSettingsModal(false);
          setSelectedStory(null);
        }}
        onUpdate={handleStoryUpdate as Parameters<typeof StorySettingsModal>[0]['onUpdate']}
        onDelete={handleStoryDelete}
      />
    </PageLayout>
  );
};

export default LibraryPage;
